import * as Sentry from '@sentry/react';
import { FC, useContext, useEffect, useRef } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import AuthContext from '@/contexts/auth/AuthContext';
import { createSentryAnonymousUser } from '@/errors/error';
import ErrorPage from '@/pages/ErrorPage';

const RouteErrorBoundary: FC = () => {
    const routeError = useRouteError();
    const { data, loading } = useContext(AuthContext);
    const capturedError = useRef<unknown>(undefined);

    useEffect(() => {
        if (loading || !routeError || capturedError.current === routeError) {
            return;
        }

        capturedError.current = routeError;

        if (data) {
            Sentry.setUser({
                id: data.userName,
                username: data.userName,
                fullName: data.displayName,
                authenticated: true
            });
        }
        else {
            Sentry.setUser(createSentryAnonymousUser());
        }

        Sentry.captureException(routeError);
    }, [data, loading, routeError]);

    const errorType = isRouteErrorResponse(routeError)
        ? String(routeError.status)
        : 'Error';
    const errorMessage = isRouteErrorResponse(routeError)
        ? (typeof routeError.data === 'string' ? routeError.data : routeError.statusText)
        : undefined;

    return (
        <ErrorPage
            errorType={errorType}
            errorMessage={errorMessage}
        />
    );
};

export default RouteErrorBoundary;
